import { useEffect, useRef, useState } from "react";
import { serverFetch } from "../../server";
import { Button, message, Table, DatePicker, Select, Input, Typography, Card, Spin } from "antd";
import { getLicenseStatusColor } from "../../extensions/helper";
import LicenseOrder from "./license-order";
import dayjs from "dayjs";
import { fmtDate } from "../../extensions/utils";
import { getOrderStatusName, orderStatuses } from "../../extensions/helper";
import { useSelector } from "react-redux";
import { RefreshButton, isMobile,  menuSize } from "../../extensions/design";
import { useLocalStorageFilter, applyDateRoll } from "@/extensions/use-local-storage-filter";
const { Text } = Typography

const LicenseOrders = ({externalFilter = null}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);

  const [filter, setFilter] = useLocalStorageFilter(
    'licenseOrdersFilter',
    () => ({
      dateFrom: dayjs().subtract(3, 'month'),
      dateTo: dayjs(),
      statuses: [],
      search: '',
    }),
    (parsed) => {
      const { dateFrom, dateTo } = applyDateRoll(
        parsed.dateTo ? dayjs(parsed.dateTo) : null,
        dayjs().subtract(3, 'month'),
        dayjs(),
        parsed.dateFrom ? dayjs(parsed.dateFrom) : null,
      );
      return {
        dateFrom: typeof dateFrom === 'string' ? dayjs(dateFrom) : dateFrom,
        dateTo: typeof dateTo === 'string' ? dayjs(dateTo) : dateTo,
        statuses: parsed.statuses || [],
        search: parsed.search || ''
      };
    },
    (filter) => ({
      dateFrom: filter.dateFrom?.format('YYYY-MM-DD'),
      dateTo: filter.dateTo?.format('YYYY-MM-DD'),
      statuses: filter.statuses || [],
      search: filter.search || ''
    }),
  );
  const [searchInput, setSearchInput] = useState(() => {
    const savedFilter = localStorage.getItem('licenseOrdersFilter');
    if (savedFilter) {
      try {
        const parsed = JSON.parse(savedFilter);
        return parsed.search || '';
      } catch (e) {
        console.warn('Ошибка при загрузке поиска из localStorage:', e);
      }
    }
    return '';
  });
  const searchDebounceRef = useRef(null);
  const divRef = useRef(null);
  const user = useSelector((s) => s.user);

  useEffect(() => {
    if (externalFilter !== null) {
      const allowedKeys = ['search', 'statuses'];
      const rawFilter = allowedKeys.reduce((acc, key) => {
        if (externalFilter[key] !== undefined) {
          acc[key] = externalFilter[key];
        }
        return acc;
      }, {});
      setSearchInput(rawFilter.search ?? "");
      const nextFilter = { dateFrom: filter.dateFrom, dateTo: filter.dateTo, ...rawFilter};
      setFilter(nextFilter);
      getData(nextFilter);
    }
  }, [externalFilter]);

  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  const getData = (f = filter) => {
    setLoading(true);
    const params = new URLSearchParams();
    
    if (f.dateFrom) params.append("DateFrom", f.dateFrom.format('YYYY-MM-DD'));
    if (f.dateTo) params.append("DateTo", f.dateTo.format('YYYY-MM-DD'));
    if (f.statuses && f.statuses.length > 0) {
      f.statuses.forEach(s => params.append("Statuses", s));
    }
    if (f.search?.trim()) params.append("search", f.search.trim());

    serverFetch(`Order?${params.toString()}`)
      .then(data => {
        setItems(data);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(getData, [filter]);

  const handlePayment = (orderId) => {
    setLoading(true);
    serverFetch(`Order/Pay/${orderId}`)
      .then(data => {
        setLoading(false);
        if (data?.url) {
          window.open(data.url, '_blank');
        }
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const renderReceipt = (url) => {
    if (!url) return null;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        <img src="/images/card.svg" alt="Чек" style={{ width: 20, cursor: 'pointer' }} />
      </a>
    );
  };

  const renderPaidOn = (paidOn, record) => {
    // Если заказ новый (status === 0) и не оплачен (paidOn === null), показываем кнопку
    if (record.status === 0 && !paidOn) return (<Button type="link" size="small" onClick={(e) => {e.stopPropagation();handlePayment(record.id);}}>Оплатить</Button>);
    return fmtDate(paidOn);
  };

  const borderColor = (item) => {
    return getLicenseStatusColor(item.status, item)
  };

  const columns = [
    { title: "#", dataIndex: "id", width: 60 },
    { title: "Компания", dataIndex: "companyName", width: 150 },
    { title: "Дата", dataIndex: "docDate", width: 100, render: fmtDate, sorter: (a, b) => new Date(a.docDate) - new Date(b.docDate), },
    { title: "Тариф", dataIndex: "tariffName", width: 200 },
    { title: "Статус", dataIndex: "status", width: 100, render: getOrderStatusName },
    { title: "Цена", dataIndex: "price", width: 90, align: "right", render: (val) => val?.toFixed(2) },
    { title: "Оплачен", dataIndex: "paidOn", width: 100, render: renderPaidOn },
    { title: "Автор", dataIndex: "authorName", width: 120 },
    { title: "Активация", dataIndex: "activated", width: 130, render: fmtDate, sorter: (a, b) => new Date(a.activated) - new Date(b.activated), },
    { title: "Окончание", dataIndex: "finished", width: 130, render: fmtDate, sorter: (a, b) => new Date(a.finished) - new Date(b.finished), },
    { title: "Чек", dataIndex: "receiptUrl", width: 60, align: "center", render: renderReceipt },
    // { title: "Срок", dataIndex: "paidDeadline", width: 100, render: fmtDate },
  ];

  const onClose = (refresh) => {
    setEditId(null);
    refresh && getData();
  };

  const headerSize = divRef.current?.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
  const tableHeight = divRef.current ? (document.documentElement.offsetHeight - headerSize - divRef.current.getBoundingClientRect().top - window.scrollY - 1) : 0;

  return !isMobile ? (
    <div className="sport-form-content">
      <div className="sport-form-title">
        <Input
          placeholder="Поиск по компании, email"
          value={searchInput}
          onChange={(e) => {
            const value = e.target.value;
            setSearchInput(value);
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = setTimeout(() => setFilter(prev => ({ ...prev, search: value })), 500);
          }}
          allowClear
          style={{ maxWidth: 420, marginRight: 8 }}
        />
        <DatePicker 
          placeholder="Дата от" 
          format="DD.MM.YYYY" 
          value={filter.dateFrom} 
          onChange={(val) => setFilter(prev => ({ ...prev, dateFrom: val }))}
          style={{ marginRight: 8 }}
        />
        <DatePicker 
          placeholder="Дата до" 
          format="DD.MM.YYYY" 
          value={filter.dateTo} 
          onChange={(val) => setFilter(prev => ({ ...prev, dateTo: val }))}
          style={{ marginRight: 8 }}
        />
        <Select
          placeholder="Статусы"
          mode="multiple"
          value={filter.statuses}
          onChange={(val) => setFilter(prev => ({ ...prev, statuses: val }))}
          options={orderStatuses}
          style={{ width: 200, marginRight: 8 }}
          allowClear
        />
        {user.isAdmin && (
          <Button onClick={() => setEditId(0)} className="search-btn">
            Новый
          </Button>
        )}
        <RefreshButton onClick={getData} />
      </div>
      <div ref={divRef} style={{ flex: 1 }}>
        <Table
          dataSource={items}
          columns={columns}
          loading={loading}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ y: tableHeight }}
          onRow={(record) => ({ 
            onClick: () => {
              if (user.isAdmin) {
                setEditId(record.id);
              }
            }
          })}
        />
      </div>
      {editId != null && <LicenseOrder id={editId} onClose={onClose} />}
    </div>
  ) : loading ? (
  <div className="report-loading">
    <Spin size="large" />
  </div> ) : (
    <>
    <div style={{display:"flex", justifyContent: "space-around", alignItems:"center"}}>
      <Text>Период с </Text>
      <DatePicker className="record-date-picker-mobile" value={filter.dateFrom} format="DD.MM.YYYY" onChange={(val) => setFilter(prev => ({ ...prev, dateFrom: val }))} allowClear={false}/>
      <Text> по </Text>
      <DatePicker className="record-date-picker-mobile" value={filter.dateTo} format="DD.MM.YYYY" onChange={(val) => setFilter(prev => ({ ...prev, dateTo: val }))} allowClear={false}/>
    </div>
    {user.isAdmin && (
      <Button onClick={() => setEditId(0)} style={{width: "96%", marginTop: "4px", marginLeft: "2%", marginRight: "2%"}}> Новый </Button>
    )}
    <div style={{ overflowY: "auto", flex: 1, paddingBottom: 8, marginTop: "6px", height: window.innerHeight - menuSize - 100 }}>
      {items?.map((item) => {
        return (
          <Card key={item.id} className="stock-card" style={{ borderLeft: `4px solid ${ borderColor(item)}`}}
            onClick={() => { if (user.isAdmin) { setEditId(item.id); }}}>
              <div className="license-orders-item-line-triple-non-even">
                <Text strong>{fmtDate(item.docDate)}</Text>
                <Text>{item.companyName}</Text>
                <Text strong style={{ textAlign: "right" }}> #{item.id} </Text>
              </div>

              <div className="license-orders-item-line-double-non-even">
                <Text>Тариф: {item.tariffName}</Text>
                <Text style={{ textAlign: "right" }}>
                  Цена: {item.price}
                </Text>
              </div>

              <div className="stock-osv-item-line-double">
                <Text>Актив.: {fmtDate(item.activated)}</Text>
                <Text style={{ textAlign: "right" }}> Оконч.: {fmtDate(item.finished)} </Text>
              </div>
          </Card>
        );
      })}
      {items.length === 0 && !loading && (
        <div style={{ textAlign: "center", marginTop: 20, fontSize: 14 }}> Нет данных </div>
      )}
    </div>
    {editId != null && <LicenseOrder id={editId} onClose={onClose} />}

    </>
  );
};

export default LicenseOrders;

